import { Banner, Button, Checkbox, Modal, Popover, Text } from '@shopify/polaris';
import { ProductVariantFitmentScope, VariantFitmentReview } from '../../app/models/Product';

export function VariantTargetPicker({ open, variants, currentVariantId, selected, disabled, onToggle, onClose, onSelect }: {
    open: boolean; variants: ProductVariantFitmentScope[]; currentVariantId: number | null; selected: number[]; disabled: boolean;
    onToggle: () => void; onClose: () => void; onSelect: (ids: number[]) => void;
}) {
    const otherIds = variants.filter(v => v.id !== currentVariantId).map(v => v.id);
    return <Popover active={open} onClose={onClose}
        activator={<Button variant="plain" accessibilityLabel="Choose variants" disabled={disabled} onClick={onToggle}>Choose…</Button>}>
        <div className="pf-variant-picker" role="group" aria-label="Choose variants">
            <Checkbox label="Select all" checked={selected.length === otherIds.length ? true : selected.length ? 'indeterminate' : false}
                disabled={disabled} onChange={checked => onSelect(checked ? otherIds : [])} />
            {selected.length > 100 && <Text as="p" tone="critical">Choose up to 100 other variants.</Text>}
            <div className="pf-variant-target-list">
                {variants.map(variant => <Checkbox key={variant.id}
                    label={`${[variant.sku, variant.title].filter(Boolean).join(' — ') || `Variant ${variant.id}`}${variant.id === currentVariantId ? ' (current)' : ''}`}
                    checked={variant.id === currentVariantId || selected.includes(variant.id)}
                    disabled={disabled || variant.id === currentVariantId}
                    onChange={checked => onSelect(checked ? [...selected, variant.id] : selected.filter(id => id !== variant.id))} />)}
            </div>
        </div>
    </Popover>;
}

export const variantHasChanges = (variant: VariantFitmentReview['variants'][number]) =>
    variant.added + variant.removed + variant.notesUpdated > 0;

// Older servers combine missing matches and harmless no-ops. Keep their review
// visible until the server supplies separate missing-match counts.
export const variantMissingMatches = (variant: VariantFitmentReview['variants'][number]) =>
    variant.missingMatches ?? variant.skipped;

export function VariantReviewDialog({ review, currentVariantId, busy, retry, error, onClose, onExclude, onSave }: {
    review: VariantFitmentReview | null; currentVariantId: number | null; busy: boolean; retry: boolean; error: string;
    onClose: () => void; onExclude: (ids: number[]) => void; onSave: () => void;
}) {
    const conflicts = review?.variants.filter(variant => variant.conflicts.length > 0) ?? [];
    const missing = review?.variants.filter(variant => variantMissingMatches(variant) > 0) ?? [];
    const affectedCount = review?.variants.filter(variantHasChanges).length ?? 0;
    const canExclude = conflicts.length > 0 && conflicts.every(variant => variant.variantId !== currentVariantId);
    const partialMatches = missing.some(variantHasChanges);
    const unknownSkips = missing.some(variant => variant.missingMatches === undefined);
    const primaryAction = conflicts.length > 0
        ? canExclude ? { content: `Exclude ${conflicts.length} and save`, loading: busy, onAction: () => onExclude(conflicts.map(v => v.variantId)) } : undefined
        : { content: retry ? 'Retry save' : `Save to ${affectedCount}`, disabled: !review?.canSave || affectedCount === 0, loading: busy, onAction: onSave };
    return <Modal open={!!review} onClose={() => !busy && !retry && onClose()}
        title={conflicts.length ? 'Fitment conflicts' : 'Some fitments don’t match'}
        primaryAction={primaryAction}
        secondaryActions={[{ content: 'Cancel', disabled: busy || retry, onAction: onClose }]}>
        <Modal.Section>
            <div className="pf-variant-dialog-stack">
                {error && <Banner tone="critical">{error}</Banner>}
                {conflicts.length > 0 ? <>
                    {!canExclude && <Text as="p">Resolve the current variant’s conflict before saving.</Text>}
                    {conflicts.map(variant => <div key={variant.variantId}>
                        <Text as="h3" variant="headingSm">{variant.label}</Text>
                        {Array.from(new Set(variant.conflicts)).map(conflict => <Text key={conflict} as="p" tone="critical">{conflict}</Text>)}
                    </div>)}
                </> : <Text as="p">
                    Updates {affectedCount} {affectedCount === 1 ? 'variant' : 'variants'}.{' '}
                    {unknownSkips ? 'Some changes are already applied or have no matching fitments.' : partialMatches
                        ? `${missing.length} ${missing.length === 1 ? 'variant has' : 'variants have'} unmatched fitments. Only matching edits will apply.`
                        : `${missing.length} ${missing.length === 1 ? 'has' : 'have'} no matching fitments.`}
                </Text>}
            </div>
        </Modal.Section>
    </Modal>;
}
